import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CalendarDateEditForm, CharacterAutocomplete, CharacterListEntry, ConfirmationDialogue, EditableCalendarDateView, EditableListView, ModalCard, TextField, } from '@datapad/common-controls';
import { AppContext, CalendarDate, createReferenceToNode, } from '@datapad/data-model';
import { assertNotUndefined } from '@datapad/utilities';
import { useTree } from '@fluidframework/react/alpha';
import { Add, Close, Delete } from '@mui/icons-material';
import { Box, Card, IconButton, Table, TableBody, TableCell, TableRow, Tooltip, Typography, } from '@mui/material';
import React from 'react';
var ModalStatus;
(function (ModalStatus) {
    ModalStatus[ModalStatus["None"] = 0] = "None";
    ModalStatus[ModalStatus["Deleting"] = 1] = "Deleting";
})(ModalStatus || (ModalStatus = {}));
export function CampaignView(props) {
    const { campaign } = props;
    useTree(campaign);
    const { getCharacter, handleCalendarLink, handleCharacterLink } = assertNotUndefined(React.useContext(AppContext));
    const [modalStatus, setModalStatus] = React.useState(ModalStatus.None);
    const maybeModal = (_jsx(EditingModal, { modalStatus: modalStatus, campaign: campaign, closeModal: () => setModalStatus(ModalStatus.None), deleteCampaign: () => {
            props.deleteCampaign();
            setModalStatus(ModalStatus.None);
        } }));
    return (_jsxs(Card, { sx: { width: '100%', height: '100%' }, children: [maybeModal, _jsxs(Box, { width: "100%", display: "flex", flexDirection: "column", children: [_jsx(Table, { sx: { width: '100%' }, children: _jsxs(TableBody, { children: [_jsxs(TableRow, { children: [_jsx(TableCell, { children: _jsx(Typography, { variant: "body1", children: _jsx("b", { children: "Name" }) }) }), _jsx(TableCell, { children: _jsx(TextField, { value: campaign.name, onChange: (value) => (campaign.name = value) }) })] }), _jsxs(TableRow, { children: [_jsx(TableCell, { children: _jsx(Typography, { variant: "body1", children: _jsx("b", { children: "Current date" }) }) }), _jsx(TableCell, { children: _jsx(DateEntry, { campaign: campaign, linkToCalendar: handleCalendarLink }) })] }), _jsxs(TableRow, { children: [_jsx(TableCell, { children: _jsx(Typography, { variant: "body1", children: _jsx("b", { children: "Active Characters" }) }) }), _jsx(TableCell, { children: _jsx(EditableListView, { list: campaign.activeCharacters, view: (characterRef) => {
                                                    const character = getCharacter(characterRef);
                                                    return _jsx(CharacterListEntry, { character: character });
                                                }, getKeyForItem: (character) => character.id, insertionForm: (list, close) => (_jsx(CharacterInsertView, { list: list, close: close, campaign: campaign })), canEdit: false }) })] }), _jsxs(TableRow, { children: [_jsx(TableCell, { children: _jsx(Typography, { variant: "body1", children: _jsx("b", { children: "Inactive Characters" }) }) }), _jsx(TableCell, { children: _jsx(EditableListView, { list: campaign.inactiveCharacters, view: (characterRef) => {
                                                    const character = getCharacter(characterRef);
                                                    return (_jsx(CharacterListEntry, { character: character, onClick: () => handleCharacterLink(character) }));
                                                }, getKeyForItem: (character) => character.id, insertionForm: (list, close) => (_jsx(CharacterInsertView, { list: list, close: close, campaign: campaign })), canEdit: false }) })] })] }) }), _jsx(Box, { width: "100%", display: "flex", flexDirection: "row-reverse", children: _jsx(Box, { id: "profile-toolbar-buttons", display: "flex", flexDirection: "row", children: _jsx(Tooltip, { title: "Delete campaign", children: _jsx(IconButton, { onClick: () => {
                                        setModalStatus(ModalStatus.Deleting);
                                    }, children: _jsx(Delete, {}) }) }) }) })] })] }));
}
function CharacterInsertView({ list, close, campaign, }) {
    useTree(list);
    useTree(campaign);
    const { getAllCharacters } = assertNotUndefined(React.useContext(AppContext));
    const [selectedCharacter, setSelectedCharacter] = React.useState(null);
    const availableCharacters = React.useMemo(() => {
        const characters = getAllCharacters();
        return characters.filter((character) => !campaign.activeCharacters.some((ref) => ref.id === character.id) &&
            !campaign.inactiveCharacters.some((ref) => ref.id === character.id));
    }, [getAllCharacters, campaign.activeCharacters, campaign.inactiveCharacters]);
    const handleAdd = React.useCallback(() => {
        if (selectedCharacter) {
            list.insertAtEnd(createReferenceToNode(selectedCharacter));
            setSelectedCharacter(null);
            close();
        }
    }, [selectedCharacter, list, close]);
    const handleKeyDown = React.useCallback((event) => {
        if (event.key === 'Escape') {
            close();
        }
        else if (event.key === 'Enter' && selectedCharacter) {
            event.preventDefault();
            handleAdd();
        }
    }, [close, selectedCharacter, handleAdd]);
    if (availableCharacters.length === 0) {
        return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, p: 1 }, children: [_jsx(Typography, { variant: "body2", color: "text.secondary", children: "No more characters available" }), _jsx(IconButton, { size: "small", onClick: close, "aria-label": "close", children: _jsx(Close, { fontSize: "small" }) })] }));
    }
    return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, p: 1 }, children: [_jsx(Box, { sx: { minWidth: 200, flexGrow: 1 }, children: _jsx(CharacterAutocomplete, { size: "small", characters: availableCharacters, value: selectedCharacter, onChange: setSelectedCharacter, label: "Add character", placeholder: "Search...", onKeyDown: handleKeyDown, autoFocus: true }) }), _jsx(IconButton, { size: "small", onClick: handleAdd, color: "primary", disabled: !selectedCharacter, "aria-label": "add", children: _jsx(Add, { fontSize: "small" }) }), _jsx(IconButton, { size: "small", onClick: close, "aria-label": "cancel", children: _jsx(Close, { fontSize: "small" }) })] }));
}
function EditingModal(props) {
    let renderedDialogue;
    switch (props.modalStatus) {
        case ModalStatus.None:
            renderedDialogue = undefined;
            break;
        case ModalStatus.Deleting:
            renderedDialogue = (_jsx(ModalCard, { open: true, children: _jsx(ConfirmationDialogue, { message: `Are you sure you wish to delete campaign "${props.campaign.name}"? This action cannot be undone.`, onConfirm: props.deleteCampaign, onCancel: props.closeModal }) }));
            break;
        default:
            throw new Error(`Unrecognized ModalStatus value: "${props.modalStatus}".`);
    }
    if (renderedDialogue === undefined) {
        return _jsx(_Fragment, {});
    }
    else {
        return (_jsx(ModalCard, { open: true, onClose: props.closeModal, children: renderedDialogue }));
    }
}
function DateEntry({ campaign, linkToCalendar, }) {
    useTree(campaign);
    const [editing, setEditing] = React.useState(false);
    if (editing) {
        return (_jsx(CalendarDateEditForm, { value: campaign.currentDate, submit: ({ year, day }) => {
                campaign.currentDate = new CalendarDate({ year, day });
                setEditing(false);
            }, cancel: () => {
                setEditing(false);
            } }));
    }
    else if (campaign.currentDate === undefined) {
        return (_jsxs(Box, { children: [_jsx(Typography, { variant: "body1", children: _jsx("i", { children: "The campaign does not have a current date set." }) }), _jsx(IconButton, { onClick: () => {
                        setEditing(true);
                    }, children: _jsx(Add, {}) })] }));
    }
    else {
        return (_jsx(EditableCalendarDateView, { date: campaign.currentDate, linkToCalendar: linkToCalendar, deleteDate: () => {
                campaign.currentDate = undefined;
            } }));
    }
}
//# sourceMappingURL=CampaignView.js.map